import type {
  Col,
  FilterState,
  MinMaxOptions,
  QueryObj,
  QueryObjFilters,
  SelectedOptions,
} from '@pequity/shared/ccp/types';
import { SORTING_TYPES } from '@pequity/shared/ui';
import { isOneOf } from '@pequity/shared/utils/arrays';
import { toString } from '@pequity/shared/utils/string';
import { cloneDeep, isEmpty, isNil } from 'lodash-es';

/**
 * The default filter state.
 *
 * @type {Object}
 */
export const createEmptyFilterState = (): FilterState => {
  return {
    selectedOptions: [],
    sorting: SORTING_TYPES.NO_SORTING,
    min: null,
    max: null,
  } satisfies FilterState;
};

/**
 * Builds a column's URL filter entry, keeping only the non-empty selected options and bounds.
 */
export const createQueryObjFilter = ({ selectedOptions, min, max }: Partial<FilterState>) => {
  const res: Partial<SelectedOptions & MinMaxOptions> = {};

  if (selectedOptions?.length) res.selectedOptions = selectedOptions;
  if (!isNil(min)) res.min = min;
  if (!isNil(max)) res.max = max;

  return res;
};

export const setFilterOrder = (colId: number, filters: QueryObjFilters | Record<string, never>) => {
  const res = cloneDeep(filters);
  res.order = res.order || [];

  const index = res.order.findIndex((id) => colId === id);
  const filtersAreEmpty = isEmpty(filters?.[colId] || []);

  if (index === -1 && !filtersAreEmpty) {
    res.order.push(colId);
  } else if (index !== -1 && filtersAreEmpty) {
    res.order.splice(index, 1);
  }

  return res;
};

export const setFilterStateInCols = <T extends Pick<Col, 'id' | 'filterState' | 'filterActive'>>(
  cols: T[],
  queryFilters: QueryObj['filters'],
  queryOrdering: QueryObj['ordering']
) => {
  return cloneDeep(cols).map((col) => {
    const colId = toString(col.id);

    // Set Filtering
    col.filterState = queryFilters[colId]
      ? { ...createEmptyFilterState(), ...queryFilters[colId] }
      : createEmptyFilterState();
    queryFilters = setFilterOrder(Number(colId), queryFilters);

    // Set sorting
    if (queryOrdering.includes(`${col.id}`)) {
      col.filterState.sorting = SORTING_TYPES.ASC;
    }
    if (queryOrdering.includes(`-${col.id}`)) {
      col.filterState.sorting = SORTING_TYPES.DESC;
    }

    // Set filterActive
    col.filterActive = Boolean(
      col.filterState.selectedOptions.length ||
      isOneOf([SORTING_TYPES.ASC, SORTING_TYPES.DESC], col.filterState.sorting) ||
      col.filterState.min ||
      col.filterState.max
    );

    return col;
  });
};
